import type { WaterTune } from '@/config/cities';
import type { FlatInst, HillInst } from './types';

/**
 * scenery/water.ts — GS1: THE WATER SYSTEM REBUILD (pure geometry planning).
 *
 * The old water was a train of per-step rotated rectangles (one `a.ponds` slab
 * per 12u course step, yaw-rotated to the local road tangent). On curves the
 * slabs fanned apart (ground wedges / crisscrossing corners), per-step ground
 * heights stepped between slabs (ledges), the 0.06-thick sheet had no shoreline
 * conformance or skirt (cameras saw under its edges), and the ripple UVs were
 * anchored per slab (a visible repeat + seam per tile). All of it confirmed in
 * screenshots across the nine cities — the owner's #1 reported bug.
 *
 * The rebuild: the zone builders still emit their slabs EXACTLY as before (the
 * main rng stream is each city's layout — untouched, byte-identical), but each
 * course-water slab now carries `arc/side/shore` metadata (types.ts). This
 * module runs as a POST-PASS (the finishClear doctrine: after every builder,
 * no rng anywhere — variety comes from index hashes) and welds each side's
 * contiguous slab run into ONE shoreline-following triangulated ribbon:
 *
 *  - a welded NEAR BAND that hugs the shore line (no fanning, no ledges, no
 *    per-slab seams — by construction),
 *  - a LAND SKIRT sunk below the terrain at the shoreline so no camera can
 *    ever sight under the sheet's edge,
 *  - a FAR FIELD swept on heavily-smoothed normals out past fog range, with a
 *    monotonic-sweep fix on hairpins (crossed far rows collapse instead of
 *    folding) and a COURSE CLAMP that stops the water short of any other pass
 *    of the looping road (the sheet must never flood a different stretch),
 *  - organic END TAPERS past zone boundaries (the river dives under the land
 *    instead of ending in a rectangular staircase),
 *  - per-row colour blending across zone colour changes (Patagonia's lake →
 *    meltwater seam), and a per-city SHORE TINT band (surf foam, salt crust,
 *    lagoon mud, milky glacial rim).
 *
 * Small free-standing ponds (NYC's park) have no metadata and become organic
 * disc ponds with a wet-bank ring + skirt, clamped out of the road corridor
 * and out of the hill domes. Everything lands in ONE merged mesh per city
 * (builders.buildWater) — one draw call, world-anchored ripple UVs.
 *
 * PURE: no three.js, no DOM, no rng — headless-testable (tests/world/water).
 */

// ---- Knobs (visual constants live with their mechanism, the hills.ts way) ----
export const WATER = {
  /** Water surface height above the local ground sample — matches the old
   *  instanced slabs' top (y + 0.1 lift + 0.03 half-thickness), so boats,
   *  icebergs, stilt houses and salt mounds still sit exactly right. */
  lift: 0.13,
  /** Row spacing along the course (= the placement STEP; track nodes are 12u
   *  apart, so finer sampling buys nothing). */
  step: 12,
  /** How far the sheet tucks IN under the land sheets at the shoreline, so a
   *  fanned grass/sand slab corner can never open a ground gap against the
   *  water. Beaches tuck deeper (under the sand strip's fanning corners). */
  innerOverlap: 1.4,
  innerOverlapBeach: 3.5,
  /** Near-band column offsets from the shoreline (welded, follows the shore). */
  bandCols: [10, 24, 40] as readonly number[],
  /** Far-field column reaches (swept on smoothed normals, clamped vs the
   *  course). The last sits past fog range from any near camera. */
  farCols: [110, 185, 260] as readonly number[],
  /** Land-side skirt: how far behind the shoreline and how deep it sinks. */
  skirtBack: 2.5,
  skirtDrop: 2.0,
  /** Far/perimeter skirt drop (deeper — it may have to duck under a foreign
   *  stretch's ground apron). */
  farSkirtDrop: 3.5,
  /** Rows of end taper past an interior run end (zone boundary).
   *  shared-2a: was 3. A run's far field reaches 260 u out; collapsing that to
   *  nothing over 3 rows (36 u of course) is an ~82° ramp, and a straight ramp
   *  across open water is exactly what the Napoli bay's up-course end read as
   *  — a ruled line drawn across the sea at route 0.30, with no zone-side
   *  feather able to touch it (`bayfront` is never called before 0.30, so the
   *  cut is the RUN's, not the zone's). 8 rows ≈ 96 u, eased (see
   *  taperWidth), turns it into a headland receding into the haze. */
  taperRows: 8,
  /** Height smoothing half-window (rows) — kills the per-step ledges without
   *  floating the sheet off a sloping shore. */
  smoothWin: 2,
  /** Far-field basis smoothing half-window (rows) — the far edge follows a
   *  low-pass coastline, not every kink of the road. */
  farSmoothWin: 6,
  /** Far columns ease from the row's grade toward the run's overall water
   *  level (so far regions of one run agree where they overlap), but never
   *  further than this from the local grade (bounds any flooding). */
  farLevelPull: 4,
  /** Stop the far sweep this close to a DIFFERENT pass of the course (its
   *  ground apron reaches 80 past the rail — the water edge + skirt must bury
   *  under it, never flood over it). */
  foreignClear: 86,
  /** A projection at least this many arc units away counts as a different
   *  pass (closer = the row's own neighbourhood). */
  foreignArcGap: 90,
  /** March resolution of the obstruction scan (corridor is ~172 wide — a 14u
   *  march cannot miss it). */
  foreignMarch: 14,
  /** Arc gap that splits two slabs into separate runs (slabs arrive every
   *  12u — anything past 3 steps is a genuine zone gap). */
  runGap: 40,
  /** Course slabs are d = step * 2.2 = 26.4 deep; free-standing ponds are
   *  12–20. The classifier boundary (metadata is authoritative when present). */
  pondMaxDepth: 24,
  /** Width of the shore tint band (foam/salt/mud), in units off the shore. */
  tintBand: 2.8,
  /** Organic shoreline jitter amplitude per shore kind (quay stays razor —
   *  a sea wall IS straight). */
  jitter: { quay: 0, beach: 2.2, salt: 2.2, mud: 1.4, glacial: 1.6 } as Record<string, number>,
  /** Pond ring segments + wet-bank ring geometry. */
  pondSegs: 22,
  pondBankWidth: 2.4,
  pondBankDrop: 0.35,
  pondSkirtDrop: 1.8,
  /** Road / hill clearance for pond placement (centre pushed until clear). */
  pondRoadMargin: 2.5,
  /** RESCUE FIT (see fitPond) — the deterministic lattice a pond the push
   *  loops could not settle is re-fitted on, in the local course frame, as
   *  offsets from its AUTHORED centre. Zero-offset entries lead so a tie can
   *  only ever resolve to "don't move". Reach: ±40 u of arc keeps a rescued
   *  NYC park pond inside the park's own bare window at Bow Bridge (±0.02 of
   *  a 2192 u course = ±44), which is where the trees aren't. */
  fitArcSteps: [0, 8, -8, 16, -16, 24, -24, 32, -32, 40, -40] as readonly number[],
  fitLatSteps: [0, -6, 6, -12, 12, -18, 18, -24, 24] as readonly number[],
  /** Radius units a candidate must win to justify one unit of displacement
   *  from the authored spot (a shrunken pond where it was authored beats a
   *  bigger one shoved down-course). */
  fitShiftPenalty: 0.14,
  /** Rescue floors — below these a disc reads as a puddle, not the authored
   *  water body, and the old drop is the better outcome. */
  fitMinLakeR: 10,
  fitMinPondR: 4.5,
} as const;

/** Shore-tint colours per WaterTune.shore kind (null = no band). */
export const SHORE_TINTS: Record<string, number | null> = {
  quay: null,
  beach: 0xf2fbf4, // white surf foam
  salt: 0xf2e8e4, // Lac Rose salt crust
  mud: 0x8a7a58, // warm lagoon mud
  glacial: 0xd8ece8, // milky rim
};

/** Pond wet-bank + skirt colours (dark wet earth / depth). */
export const POND_BANK = 0x46412f;
export const POND_BANK_OUT = 0x33301f;

// ---- Narrowed track access (stubable in tests, satisfied by world/Track) ----
export interface WaterSample {
  x: number;
  y: number;
  z: number;
  dir: number;
  rightX: number;
  rightZ: number;
}
export interface WaterTrackLike {
  halfWidth: number;
  length: number;
  finishDistance: number;
  sampleAt(distance: number): WaterSample;
  project(x: number, z: number): { distance: number; lateral: number; y: number };
}

// ---- Deterministic hash (the builders.ts convention — index-derived variety,
//      never rng, so layouts stay byte-identical). ----
export function hash(n: number): number {
  const s = Math.sin(n * 12.9898) * 43758.5453;
  return s - Math.floor(s);
}

/** Low-frequency organic 1D noise from a row index (−1..1). */
function shoreNoise(i: number, phase: number): number {
  return Math.sin(i * 0.37 + phase * 6.2832) * 0.62 + Math.sin(i * 0.117 + phase * 12.94) * 0.38;
}

/** Per-channel hex colour lerp (gamma-space — fine for the close water hues). */
export function mixHex(a: number, b: number, t: number): number {
  const ar = (a >> 16) & 255;
  const ag = (a >> 8) & 255;
  const ab = a & 255;
  const br = (b >> 16) & 255;
  const bg = (b >> 8) & 255;
  const bb = b & 255;
  const r = Math.round(ar + (br - ar) * t);
  const g = Math.round(ag + (bg - ag) * t);
  const bl = Math.round(ab + (bb - ab) * t);
  return (r << 16) | (g << 8) | bl;
}

export function darkenHex(c: number, f: number): number {
  return mixHex(0x000000, c, f);
}

// ---- Run collection --------------------------------------------------------

export interface WaterSlab {
  arc: number;
  side: -1 | 1;
  shore: number;
  color: number;
}
export interface WaterRun {
  side: -1 | 1;
  /** Sorted by arc, contiguous (gaps < WATER.runGap). */
  slabs: WaterSlab[];
}

export interface SplitWater {
  runs: WaterRun[];
  /** Free-standing pond slabs (no course metadata) — NYC's park ponds. */
  ponds: FlatInst[];
}

/** Split the emitted pond instances into contiguous course-water RUNS (per
 *  side) and free-standing ponds. Does NOT mutate the input. */
export function splitWater(ponds: readonly FlatInst[]): SplitWater {
  const slabs: WaterSlab[] = [];
  const loose: FlatInst[] = [];
  for (const f of ponds) {
    if (f.arc != null && f.side != null && f.shore != null && f.d >= WATER.pondMaxDepth) {
      slabs.push({ arc: f.arc, side: f.side, shore: f.shore, color: f.color });
    } else {
      loose.push(f);
    }
  }
  slabs.sort((a, b) => (a.side - b.side) * 1e9 + (a.arc - b.arc));
  const runs: WaterRun[] = [];
  for (const s of slabs) {
    const last = runs[runs.length - 1];
    if (last && last.side === s.side && s.arc - last.slabs[last.slabs.length - 1].arc <= WATER.runGap) {
      last.slabs.push(s);
    } else {
      runs.push({ side: s.side, slabs: [s] });
    }
  }
  // A degenerate 1–2 slab "run" (a zone sliver) has no coastline to speak of —
  // hand it back as a pond so it still renders as water.
  const real: WaterRun[] = [];
  for (const r of runs) {
    if (r.slabs.length >= 3) real.push(r);
    else {
      for (const s of r.slabs) {
        loose.push({
          x: 0, // resolved from arc by the caller (builders) — see pondFromSlab
          y: 0,
          z: 0,
          rotY: 0,
          w: 26,
          d: 20,
          color: s.color,
          arc: s.arc,
          side: s.side,
          shore: s.shore,
        });
      }
    }
  }
  return { runs: real, ponds: loose };
}

// ---- The run grid (the shoreline ribbon + far field) ------------------------

/** One water surface as a rows×cols vertex lattice, ready for triangulation.
 *  Column 0 is the land skirt; the last column is the far/perimeter skirt. */
export interface WaterGrid {
  rows: number;
  cols: number;
  /** rows*cols*3 world-space positions. */
  pos: number[];
  /** rows*cols vertex colours (hex). */
  color: number[];
  /** Ripple UV-density multiplier for this whole surface. */
  ripple: number;
}

export interface WaterCfg {
  shore: 'quay' | 'beach' | 'salt' | 'mud' | 'glacial';
  ripple: number;
  recolor: Map<number, number>;
}

export function resolveWaterCfg(tune: WaterTune | undefined): WaterCfg {
  const recolor = new Map<number, number>();
  for (const r of tune?.recolor ?? []) recolor.set(r.from, r.to);
  return {
    shore: tune?.shore ?? 'quay',
    ripple: tune?.ripple ?? 1,
    recolor,
  };
}

function smoothSeries(vals: number[], win: number): number[] {
  const out = new Array<number>(vals.length);
  for (let i = 0; i < vals.length; i++) {
    let sum = 0;
    let n = 0;
    for (let k = -win; k <= win; k++) {
      const j = i + k;
      if (j < 0 || j >= vals.length) continue;
      sum += vals[j];
      n++;
    }
    out[i] = sum / n;
  }
  return out;
}

/** One run's row arcs + the SHORELINE lateral at each of them — i.e. where the
 *  welded sheet's land edge sits. Extracted from buildRunGrid so anything that
 *  needs to know "is this spot water?" reads the SAME numbers the sheet is
 *  built from (the finishClear doctrine: one source of truth for the rule and
 *  the builder). */
export interface RunShoreline {
  /** Row arc distances, `WATER.step` apart. */
  arcs: number[];
  /** Shoreline |lateral| per row (absolute, on the run's own side). */
  inner: number[];
  /** The run reaches the course start / the finish line (no end taper there). */
  atStart: boolean;
  atEnd: boolean;
}

/** Row arcs + shoreline laterals for one contiguous run. PURE. */
export function runShoreline(
  run: WaterRun,
  track: WaterTrackLike,
  cfg: WaterCfg,
  runIdx = 0,
): RunShoreline {
  const hw = track.halfWidth;
  const first = run.slabs[0].arc;
  const last = run.slabs[run.slabs.length - 1].arc;
  const atStart = first < 46; // the run reaches the course start
  const atEnd = last > track.finishDistance - 46; // …or runs to the line
  const step = WATER.step;

  // Row arc list: the slab range, extended past interior ends for the taper
  // and out to the course edges when the water genuinely reaches them.
  const arcs: number[] = [];
  const start = atStart ? Math.max(2, first - 2 * step) : first;
  const end = atEnd ? Math.min(track.length - 2, last + 5 * step) : last;
  for (let t = start; t <= end + 0.001; t += step) arcs.push(t);

  // Per-row shore, straight from the slabs (interpolated).
  const slabArcs = run.slabs.map((s) => s.arc);
  const shoreAt = (t: number): number => {
    if (t <= slabArcs[0]) return run.slabs[0].shore;
    if (t >= slabArcs[slabArcs.length - 1]) return run.slabs[run.slabs.length - 1].shore;
    let i = 0;
    while (i < slabArcs.length - 1 && slabArcs[i + 1] < t) i++;
    const a = run.slabs[i];
    const b = run.slabs[Math.min(i + 1, run.slabs.length - 1)];
    const span = b.arc - a.arc || 1;
    return a.shore + ((t - a.arc) / span) * (b.shore - a.shore);
  };

  // Shoreline lateral per row: analytic shore − overlap (tucked under land),
  // organic jitter for natural shores, hard floor at the guardrail verge.
  const jitterAmp = WATER.jitter[cfg.shore] ?? 0;
  const overlap = cfg.shore === 'beach' ? WATER.innerOverlapBeach : WATER.innerOverlap;
  const innerRaw = arcs.map((t, i) => {
    const shore = shoreAt(t);
    const tucked = shore > hw + 2.2 ? shore - overlap : shore;
    const jit = jitterAmp * shoreNoise(i, hash(runIdx * 17 + 3));
    return Math.max(hw + 1, tucked + jit);
  });
  return { arcs, inner: smoothSeries(innerRaw, 1), atStart, atEnd };
}

/** Where the city's welded COURSE water begins, as a query over (arc, side):
 *  the shoreline |lateral| there, or null when that side carries no water at
 *  that arc. Built from the same slab metadata (and the same runShoreline) the
 *  sheet is welded from, so the answer can never drift from what renders.
 *
 *  WHY IT EXISTS (shared-2b): the finish plaza used to plant its pennant line
 *  down BOTH rollout verges unconditionally, at hw + 2.1 — but three cities'
 *  finish water comes in to hw + 1 (Patagonia's meltwater, the Thames, Tokyo
 *  Bay), so five of the twelve poles stood in open water with no base and no
 *  waterline. Measured, both seeds. See finishPlaza's pennant loop.
 *
 *  Free-standing disc ponds are deliberately NOT included: they are small,
 *  authored, mid-course park water (NYC's pair at Bow Bridge) and never reach
 *  a finish rollout — verified against the built lattices on both seeds. */
export function waterShorelines(
  ponds: readonly FlatInst[],
  track: WaterTrackLike,
  cfg: WaterCfg,
): (arc: number, side: -1 | 1) => number | null {
  const lines: { side: -1 | 1; s: RunShoreline }[] = [];
  splitWater(ponds).runs.forEach((run, i) =>
    lines.push({ side: run.side, s: runShoreline(run, track, cfg, i) }),
  );
  return (arc, side) => {
    let best: number | null = null;
    for (const { side: rs, s } of lines) {
      if (rs !== side) continue;
      const { arcs, inner, atStart, atEnd } = s;
      // The sheet reaches past the row list: half a step at a genuine world
      // edge, `taperRows` steps where it tapers away. Covering the taper is
      // the conservative direction for a "don't stand furniture here" test.
      const lo = arcs[0] - (atStart ? 0.5 : WATER.taperRows) * WATER.step;
      const hi = arcs[arcs.length - 1] + (atEnd ? 0.5 : WATER.taperRows) * WATER.step;
      if (arc < lo || arc > hi) continue;
      // Linear interpolation between rows; clamped at the ends.
      const f = (arc - arcs[0]) / WATER.step;
      const i0 = Math.max(0, Math.min(inner.length - 1, Math.floor(f)));
      const i1 = Math.max(0, Math.min(inner.length - 1, i0 + 1));
      const t = Math.max(0, Math.min(1, f - i0));
      const v = inner[i0] + (inner[i1] - inner[i0]) * t;
      if (best == null || v < best) best = v; // the nearest-in shoreline wins
    }
    return best;
  };
}

/** Weld one contiguous slab run into a shoreline-following water grid. */
export function buildRunGrid(run: WaterRun, track: WaterTrackLike, cfg: WaterCfg, runIdx = 0): WaterGrid {
  const side = run.side;
  const { arcs, inner, atStart, atEnd } = runShoreline(run, track, cfg, runIdx);
  const step = WATER.step;
  const slabArcs = run.slabs.map((s) => s.arc);
  // Colours: recolour first, then box-smooth per channel across ±4 slabs so a
  // zone colour change (Patagonia lake → meltwater) becomes a ~100u gradient.
  const slabColors = run.slabs.map((s) => cfg.recolor.get(s.color) ?? s.color);
  const smoothColor = (i: number): number => {
    let r = 0;
    let g = 0;
    let b = 0;
    let n = 0;
    for (let k = -4; k <= 4; k++) {
      const j = Math.min(slabColors.length - 1, Math.max(0, i + k));
      const c = slabColors[j];
      r += (c >> 16) & 255;
      g += (c >> 8) & 255;
      b += c & 255;
      n++;
    }
    return ((Math.round(r / n) & 255) << 16) | ((Math.round(g / n) & 255) << 8) | (Math.round(b / n) & 255);
  };
  const colorAt = (t: number): number => {
    let i = 0;
    while (i < slabArcs.length - 1 && slabArcs[i + 1] < t) i++;
    const a = smoothColor(i);
    const b = smoothColor(Math.min(i + 1, slabColors.length - 1));
    const span = (slabArcs[Math.min(i + 1, slabArcs.length - 1)] - slabArcs[i]) || 1;
    const tt = Math.min(1, Math.max(0, (t - slabArcs[i]) / span));
    return mixHex(a, b, tt);
  };

  const samples = arcs.map((t) => track.sampleAt(t));
  // Smooth the water level along the run (kills the per-step ledges) but
  // NEVER let it dip below the exact terrain sample: the ground ribbon is
  // flat across at road height, so a surface even 0.05 under it lets green
  // crest slivers knife through the water (measured on lagos). max() keeps
  // ≥ at every row, and linear interpolation preserves it between rows.
  const groundY = smoothSeries(samples.map((s) => s.y), WATER.smoothWin).map((v, i) =>
    Math.max(v, samples[i].y),
  );
  const runLevel = [...groundY].sort((a, b) => a - b)[Math.floor(groundY.length / 2)];

  // Far-field basis: shore points smoothed hard, normals renormalised — the
  // far edge follows a low-pass coastline instead of every road kink.
  const shorePts = arcs.map((_t, i) => {
    const smp = samples[i];
    return {
      x: smp.x + smp.rightX * side * inner[i],
      z: smp.z + smp.rightZ * side * inner[i],
    };
  });
  const win = WATER.farSmoothWin;
  const smX = smoothSeries(shorePts.map((p) => p.x), win);
  const smZ = smoothSeries(shorePts.map((p) => p.z), win);
  const smNx: number[] = [];
  const smNz: number[] = [];
  for (let i = 0; i < arcs.length; i++) {
    let nx = 0;
    let nz = 0;
    let n = 0;
    for (let k = -win; k <= win; k++) {
      const j = Math.min(arcs.length - 1, Math.max(0, i + k));
      nx += samples[j].rightX * side;
      nz += samples[j].rightZ * side;
      n++;
    }
    void n;
    const len = Math.hypot(nx, nz) || 1;
    smNx.push(nx / len);
    smNz.push(nz / len);
  }
  // Smoothed tangents (for the monotonic far sweep).
  const smTx: number[] = [];
  const smTz: number[] = [];
  for (let i = 0; i < arcs.length; i++) {
    const a = Math.max(0, i - 1);
    const b = Math.min(arcs.length - 1, i + 1);
    let tx = smX[b] - smX[a];
    let tz = smZ[b] - smZ[a];
    const len = Math.hypot(tx, tz) || 1;
    tx /= len;
    tz /= len;
    smTx.push(tx);
    smTz.push(tz);
  }

  // COURSE CLAMP: how far each row's far ray may reach before it would flood a
  // different pass of the looping course (its ground apron reaches ~80 past
  // the rail — the water edge + skirt must bury before it, never wash over).
  const bandEnd = WATER.bandCols[WATER.bandCols.length - 1];
  const farMax = WATER.farCols[WATER.farCols.length - 1];
  const obstruction: number[] = [];
  for (let i = 0; i < arcs.length; i++) {
    let stop = farMax + WATER.foreignMarch;
    for (let L = bandEnd + 8; L <= farMax; L += WATER.foreignMarch) {
      const px = smX[i] + smNx[i] * L;
      const pz = smZ[i] + smNz[i] * L;
      const pr = track.project(px, pz);
      if (Math.abs(pr.lateral) < WATER.foreignClear && Math.abs(pr.distance - arcs[i]) > WATER.foreignArcGap) {
        stop = L;
        break;
      }
    }
    obstruction.push(stop);
  }
  // Smooth the clamp a little so the water's far edge doesn't scallop.
  const obSm = smoothSeries(obstruction, 2).map((v, i) => Math.min(v, obstruction[i]));

  // ---- Emit the lattice. Columns:
  //  0 land skirt · 1 shoreline (tint) · 2 tint-band end · 3..5 near band ·
  //  6..8 far field · 9 far skirt.
  const cols = 3 + WATER.bandCols.length + WATER.farCols.length + 1;
  const pos: number[] = [];
  const color: number[] = [];
  const tint = SHORE_TINTS[cfg.shore];

  // Interior end tapers: extra synthetic rows past each interior end that
  // shrink the sheet toward the shore and dive it under the terrain — the
  // river ends by slipping beneath the land, never in a rectangular cut.
  interface RowSpec {
    i: number; // basis row index
    widthScale: number; // 1 = full sheet, 0 = collapsed at the shore
    sink: number; // 0 = at surface, 1 = fully buried
    arcOff: number; // extra arc offset for taper rows (world units)
  }
  const rowSpecs: RowSpec[] = [];
  // shared-2a: the taper profile. `pow(1-t, 1.2)` sheds width fastest at the
  // START of the taper, which is what drew the straight diagonal — the far
  // edge dived for the shore immediately and then crawled. An ELLIPTICAL
  // quarter holds the width while the run recedes and only closes at the very
  // end, i.e. a rounded headland; a small per-row hash jitter keeps the arc
  // off a drawing-compass perfect curve. The sink is delayed (t^1.6) so the
  // sheet is still at surface level while it is visibly wide, and is fully
  // buried by the last row (the taper test's contract).
  const taperWidth = (t: number, k: number): number =>
    Math.max(0, Math.sqrt(Math.max(0, 1 - t * t)) * (0.93 + 0.14 * hash(k * 7 + runIdx * 31)));
  if (!atStart) {
    for (let k = WATER.taperRows; k >= 1; k--) {
      const t = k / WATER.taperRows;
      rowSpecs.push({
        i: 0,
        widthScale: taperWidth(t, k),
        sink: Math.pow(t, 1.6),
        arcOff: -k * step,
      });
    }
  }
  for (let i = 0; i < arcs.length; i++) rowSpecs.push({ i, widthScale: 1, sink: 0, arcOff: 0 });
  if (!atEnd) {
    for (let k = 1; k <= WATER.taperRows; k++) {
      const t = k / WATER.taperRows;
      rowSpecs.push({
        i: arcs.length - 1,
        widthScale: taperWidth(t, k + 64),
        sink: Math.pow(t, 1.6),
        arcOff: k * step,
      });
    }
  } else {
    // The water genuinely reaches the world's end: one perimeter dive row.
    rowSpecs.push({ i: arcs.length - 1, widthScale: 1, sink: 1, arcOff: 0.5 * step });
  }
  if (atStart) rowSpecs.unshift({ i: 0, widthScale: 1, sink: 1, arcOff: -0.5 * step });

  // Far-column world points get the monotonic sweep fix per column: a far row
  // that would fold BACKWARD along the smoothed tangent collapses onto its
  // predecessor instead (hairpins fan from a point instead of crisscrossing).
  const lastFar: { x: number; z: number }[] = WATER.farCols.map(() => ({ x: NaN, z: NaN }));
  // shared-2b: THE SAME MONOTONIC SWEEP FIX ON THE NEAR BAND. The far columns
  // have collapsed backward-folding rows since GS1; the near band never did —
  // and it is welded to the shore, which follows EVERY kink of the road, so on
  // a tight loop the band (40 u of reach) fans straight across itself. Measured
  // on Patagonia's lake at seed 123: rows 42, 44 AND 46 of the same run all
  // cover world (73.3, 1264.8), 0.14–0.29 u apart in height. The crossing quads
  // stand near-vertical, computeVertexNormals shades them dark, and the result
  // reads as a grey mottled crest with two hairline slivers sitting out in the
  // middle of the lake — "an island that isn't an island", with icebergs on it.
  // MEASURED DEAD ENDS (do not re-try): capping the near band at the local
  // terrain the way clampFarField caps the far columns does NOT fire here at
  // all (the fold sits +0.14–0.29 over grade, the cap allows +0.76) while it
  // WOULD move water in seven other cities; and the artifact is byte-identical
  // with theme.apronSink disabled, so the ground window is not the cause.
  const lastBand: { x: number; z: number }[] = WATER.bandCols.map(() => ({ x: NaN, z: NaN }));

  for (const spec of rowSpecs) {
    const i = spec.i;
    const smp =
      spec.arcOff === 0
        ? samples[i]
        : track.sampleAt(Math.min(track.length - 1, Math.max(1, arcs[i] + spec.arcOff)));
    const gY = spec.arcOff === 0 ? groundY[i] : smp.y;
    const surfY = gY + WATER.lift - spec.sink * (WATER.lift + 1.9);
    const inLat = inner[i];
    const rowColor = colorAt(arcs[i] + spec.arcOff);
    const shoreX = spec.arcOff === 0 ? shorePts[i].x : smp.x + smp.rightX * side * inLat;
    const shoreZ = spec.arcOff === 0 ? shorePts[i].z : smp.z + smp.rightZ * side * inLat;
    const rx = smp.rightX * side;
    const rz = smp.rightZ * side;

    const push = (x: number, y: number, z: number, c: number): void => {
      pos.push(x, y, z);
      color.push(c);
    };

    // 0 — land skirt (buried behind the shoreline).
    push(
      shoreX - rx * WATER.skirtBack,
      gY - WATER.skirtDrop - spec.sink * 1.2,
      shoreZ - rz * WATER.skirtBack,
      darkenHex(rowColor, 0.42),
    );
    // 1 — the shoreline itself (wears the shore tint).
    const shoreC = tint != null && spec.sink === 0 ? mixHex(rowColor, tint, 0.8) : rowColor;
    push(shoreX, surfY, shoreZ, shoreC);
    // 2 — tint band end.
    const bandL = WATER.tintBand * spec.widthScale;
    push(shoreX + rx * bandL, surfY, shoreZ + rz * bandL, rowColor);
    // 3..5 — the near band (welded, follows the shore exactly) + the monotonic
    // sweep: a column point that would fold BACKWARD along the smoothed
    // tangent collapses onto its predecessor, so a hairpin's band fans from a
    // point instead of crisscrossing itself. Taper rows are excluded (they are
    // already collapsing toward the shore), exactly as the far field does.
    for (let bi = 0; bi < WATER.bandCols.length; bi++) {
      const L = WATER.bandCols[bi] * spec.widthScale;
      let bx = shoreX + rx * L;
      let bz = shoreZ + rz * L;
      const prevB = lastBand[bi];
      if (spec.widthScale === 1 && Number.isFinite(prevB.x)) {
        const fwd = (bx - prevB.x) * smTx[i] + (bz - prevB.z) * smTz[i];
        if (fwd < 0.3) {
          bx = prevB.x;
          bz = prevB.z;
        }
      }
      if (spec.widthScale === 1) {
        prevB.x = bx;
        prevB.z = bz;
      }
      push(bx, surfY, bz, rowColor);
    }
    // 6..8 — the far field on the smoothed basis (+ clamp + monotonic sweep).
    const farBaseX = spec.arcOff === 0 ? smX[i] : shoreX;
    const farBaseZ = spec.arcOff === 0 ? smZ[i] : shoreZ;
    const stop = obSm[i] - 10;
    for (let f = 0; f < WATER.farCols.length; f++) {
      const want = WATER.farCols[f] * spec.widthScale;
      const L = Math.max(bandEnd * spec.widthScale + 2, Math.min(want, stop));
      let fx = farBaseX + smNx[i] * L;
      let fz = farBaseZ + smNz[i] * L;
      const prev = lastFar[f];
      if (spec.widthScale === 1 && Number.isFinite(prev.x)) {
        const fwd = (fx - prev.x) * smTx[i] + (fz - prev.z) * smTz[i];
        if (fwd < 0.3) {
          fx = prev.x;
          fz = prev.z;
        }
      }
      if (spec.widthScale === 1) {
        prev.x = fx;
        prev.z = fz;
      }
      // Far water eases toward the run's overall level (so distant regions of
      // one run agree where they overlap) — but only ever UP from the local
      // grade: dropping below it would slide the sheet under the run's own
      // flat ground apron (green strips knifing through, the old bug class).
      const ease = [0.55, 0.8, 1][f] ?? 1;
      let fy = gY + (runLevel - gY) * ease;
      fy = Math.min(gY + WATER.farLevelPull, Math.max(gY, fy));
      fy += WATER.lift - spec.sink * (WATER.lift + 1.9);
      push(fx, fy, fz, rowColor);
      if (f === WATER.farCols.length - 1) {
        // 9 — far skirt: fold the edge down so it can bury under a foreign
        // stretch's ground apron (sized against the ground actually there).
        const pr = track.project(fx, fz);
        const drop = Math.max(WATER.farSkirtDrop, fy - pr.y + 2.5);
        push(fx + smNx[i] * 2, fy - drop, fz + smNz[i] * 2, darkenHex(rowColor, 0.5));
      }
    }
  }

  return { rows: rowSpecs.length, cols, pos, color, ripple: cfg.ripple };
}

// ---- Ponds (organic discs with a wet bank — NYC's park) ---------------------

export interface PondPlan {
  x: number;
  z: number;
  y: number;
  r: number;
  color: number;
  ripple: number;
  idx: number;
}

/** The shipped pond-vs-dome standard: a disc is clear of a dome once its
 *  centre keeps this distance. Deliberately not "fully outside the footprint"
 *  — the park's hill field is dense enough that a strict rule leaves NO room
 *  for water at all (measured: no disc of r ≥ 5 fits anywhere within ±40 u of
 *  Bow Bridge on either seed), and every pond that ships today sits inside
 *  this bound. Acceptance AND the rescue fit read it, so they can never
 *  disagree about what "settled" means (the push loop below deliberately aims
 *  a hair further out, 0.8/0.7, so a push clears the bar instead of landing
 *  exactly on it). */
function domeClear(h: HillInst, r: number): number {
  return h.r * 0.75 + r * 0.6;
}

/** Largest radius (capped at `want`) a disc centred here could have and still
 *  clear the road corridor and every hill dome. Negative = the centre itself
 *  is already inside something. */
function fitRadiusAt(
  x: number,
  z: number,
  want: number,
  track: WaterTrackLike,
  hills: readonly HillInst[],
): number {
  const pr = track.project(x, z);
  let r = Math.min(want, Math.abs(pr.lateral) - (track.halfWidth + WATER.pondRoadMargin));
  for (const h of hills) {
    if (r <= 0) break;
    // domeClear inverted for r: d ≥ h.r*0.75 + r*0.6  →  r ≤ (d − h.r*0.75)/0.6
    r = Math.min(r, (Math.hypot(x - h.x, z - h.z) - h.r * 0.75) / 0.6);
  }
  return r;
}

/** THE RESCUE (nyc-lake round). A pond the push loops cannot settle used to be
 *  DROPPED outright ("better no pond than a clipped one") — and a big disc in
 *  a hill field can never settle, because the push is radial: every dome the
 *  park sets back from the road sits OUTBOARD of the water, so escaping it
 *  shoves the disc straight at the road, where the road check then shrinks and
 *  finally rejects it. Measured: NYC's authored −52 lake at Bow Bridge died
 *  that way on BOTH seeds (pushed from lateral −52 to −28 with r 30, i.e. onto
 *  the carriageway) and took three park ponds with it — half the composition
 *  the city authors simply never rendered, leaving bare navy ground beside a
 *  finished lake.
 *
 *  So: FIT, don't drop. Search a small deterministic lattice around the
 *  AUTHORED centre (never the pushed one — that is the wreckage) and take the
 *  biggest disc that genuinely clears road + domes, paying `fitShiftPenalty`
 *  per unit of displacement so a shrunken pond where it was authored beats a
 *  bigger one shoved down-course. Only when nothing above the floor fits
 *  anywhere does the old drop still stand. */
export function fitPond(
  x0: number,
  z0: number,
  want: number,
  minR: number,
  track: WaterTrackLike,
  hills: readonly HillInst[],
): { x: number; z: number; r: number } | null {
  const p0 = track.project(x0, z0);
  const sign = p0.lateral >= 0 ? 1 : -1;
  const lat0 = Math.abs(p0.lateral);
  let best: { x: number; z: number; r: number } | null = null;
  let bestScore = -Infinity;
  for (const dArc of WATER.fitArcSteps) {
    const arc = p0.distance + dArc;
    if (arc < 0 || arc > track.length) continue;
    const s = track.sampleAt(arc);
    for (const dLat of WATER.fitLatSteps) {
      const lat = lat0 + dLat;
      if (lat <= 0) continue;
      const x = s.x + s.rightX * sign * lat;
      const z = s.z + s.rightZ * sign * lat;
      const r = fitRadiusAt(x, z, want, track, hills);
      if (r < minR) continue;
      const score = r - WATER.fitShiftPenalty * Math.hypot(dArc, dLat);
      if (score > bestScore) {
        bestScore = score;
        best = { x, z, r };
      }
    }
  }
  return best;
}

/** Resolve, clamp and de-conflict the free-standing ponds: out of the road
 *  corridor, out of the hill domes (the domes used to clip ponds mid-slope).
 *  Anything the pushes cannot settle is re-fitted rather than deleted (see
 *  fitPond). Deterministic — pushes and fits are geometric, no rng. */
export function planPonds(
  ponds: readonly FlatInst[],
  lakes: readonly { at: number; lateral: number; r: number; color: number }[],
  track: WaterTrackLike,
  hills: readonly HillInst[],
  cfg: WaterCfg,
): PondPlan[] {
  const hw = track.halfWidth;
  const out: PondPlan[] = [];
  const specs: { x: number; z: number; r: number; color: number; lake: boolean }[] = [];
  for (const f of ponds) {
    if (f.arc != null && f.x === 0 && f.z === 0) {
      // A degenerate run sliver handed back by splitWater — resolve from arc.
      const s = track.sampleAt(f.arc);
      const lat = (f.shore ?? hw + 6) + 10;
      specs.push({
        x: s.x + s.rightX * (f.side ?? 1) * lat,
        z: s.z + s.rightZ * (f.side ?? 1) * lat,
        r: 10,
        color: f.color,
        lake: false,
      });
    } else {
      specs.push({ x: f.x, z: f.z, r: (f.w + f.d) / 4, color: f.color, lake: false });
    }
  }
  for (const l of lakes) {
    const s = track.sampleAt(l.at * track.finishDistance);
    specs.push({
      x: s.x + s.rightX * l.lateral,
      z: s.z + s.rightZ * l.lateral,
      r: l.r,
      color: l.color,
      lake: true,
    });
  }

  specs.forEach((spec, idx) => {
    let { x, z, r } = spec;
    // 1 — off the road: push the centre straight out until the disc clears.
    for (let iter = 0; iter < 3; iter++) {
      const p = track.project(x, z);
      const clear = Math.abs(p.lateral) - r - (hw + WATER.pondRoadMargin);
      if (clear >= 0) break;
      const s = track.sampleAt(p.distance);
      const sign = p.lateral >= 0 ? 1 : -1;
      const target = sign * (hw + WATER.pondRoadMargin + r + 0.5);
      x = s.x + s.rightX * target;
      z = s.z + s.rightZ * target;
    }
    // 2 — out of the hill domes (a pond stuck in a dome read as a tilted
    // trapezoid mid-slope — measured in NYC). Push away; shrink if cornered.
    for (let iter = 0; iter < 4; iter++) {
      let worst: HillInst | null = null;
      let worstOver = 0;
      for (const h of hills) {
        const d = Math.hypot(x - h.x, z - h.z);
        const over = h.r * 0.8 + r * 0.7 - d;
        if (over > worstOver) {
          worstOver = over;
          worst = h;
        }
      }
      if (!worst) break;
      const d = Math.hypot(x - worst.x, z - worst.z) || 1;
      x += ((x - worst.x) / d) * (worstOver + 1);
      z += ((z - worst.z) / d) * (worstOver + 1);
      // Re-check the road after a hill push.
      const p = track.project(x, z);
      if (Math.abs(p.lateral) - r < hw + WATER.pondRoadMargin) {
        r *= 0.82; // cornered between hill + road — shrink instead
      }
    }
    // Final acceptance: still overlapping either → the pushes failed, so
    // re-fit the disc around its AUTHORED centre instead of deleting it (see
    // fitPond). Only a spec that fits nowhere above its floor is dropped.
    let ok = Math.abs(track.project(x, z).lateral) - r >= hw + WATER.pondRoadMargin - 0.5;
    if (ok) {
      for (const h of hills) {
        if (Math.hypot(x - h.x, z - h.z) < domeClear(h, r)) {
          ok = false;
          break;
        }
      }
    }
    if (!ok) {
      const fitted = fitPond(
        spec.x,
        spec.z,
        spec.r,
        spec.lake ? WATER.fitMinLakeR : WATER.fitMinPondR,
        track,
        hills,
      );
      if (!fitted) return;
      x = fitted.x;
      z = fitted.z;
      r = fitted.r;
    }
    const p = track.project(x, z);
    out.push({
      x,
      z,
      y: p.y,
      r,
      color: cfg.recolor.get(spec.color) ?? spec.color,
      // Small ponds get a denser ripple so they never read as one giant
      // "plaid" swirl (measured: the world-scale ripple is ~14u per feature).
      ripple: Math.min(3, Math.max(1.2, 3.4 - r * 0.055)) * cfg.ripple,
      idx,
    });
  });
  return out;
}

/** An organic pond disc as a lattice: centre fan → surface ring → wet-bank
 *  ring → skirt ring (wrapped — the last column duplicates the first). */
export function buildPondGrid(p: PondPlan): WaterGrid {
  const n = WATER.pondSegs;
  const cols = n + 1;
  const pos: number[] = [];
  const color: number[] = [];
  const ph1 = hash(p.idx * 7 + 1) * Math.PI * 2;
  const ph2 = hash(p.idx * 7 + 2) * Math.PI * 2;
  const ph3 = hash(p.idx * 7 + 3) * Math.PI * 2;
  const radiusAt = (th: number): number =>
    p.r * (1 + 0.16 * Math.sin(2 * th + ph1) + 0.11 * Math.sin(3 * th + ph2) + 0.06 * Math.sin(5 * th + ph3));

  const surfY = p.y + WATER.lift;
  const rows: { rMul: number; y: number; c: number; centre?: boolean }[] = [
    { rMul: 0, y: surfY, c: p.color, centre: true },
    { rMul: 1, y: surfY, c: p.color },
    { rMul: 1, y: p.y + 0.04, c: POND_BANK }, // bank inner lip, just under the surface
    { rMul: 1, y: p.y - WATER.pondBankDrop, c: POND_BANK_OUT }, // bank outer, under the grass top
    { rMul: 1, y: p.y - WATER.pondSkirtDrop, c: darkenHex(p.color, 0.35) },
  ];
  const rExtra = [0, 0, 0.3, WATER.pondBankWidth, WATER.pondBankWidth + 1.2];
  rows.forEach((row, ri) => {
    for (let k = 0; k <= n; k++) {
      const th = ((k % n) / n) * Math.PI * 2;
      const rr = row.centre ? 0 : radiusAt(th) + rExtra[ri];
      pos.push(p.x + Math.cos(th) * rr, row.y, p.z + Math.sin(th) * rr);
      color.push(row.c);
    }
  });
  return { rows: rows.length, cols, pos, color, ripple: p.ripple };
}

/** How far inboard of a shoreline a trunk must stand (world units). */
export const SHORE_TREE_MARGIN = 2.6;

/** Pull vegetation that ended up BEYOND a shoreline back onto the bank.
 *
 *  WHY THIS EXISTS. `hills.nudgeTreesOffLakes` pushes a tree swallowed by an
 *  authored lake OUTWARD to the lake rim. That was correct while "off the
 *  lake" meant "on land" — but NYC's Central Park apron is welded course water
 *  now, so the rim of the Bow Bridge lakes sits INSIDE the reservoir and the
 *  nudge parks trees in open water (measured: 9-10 of them, every offender at
 *  exactly `dist == need`, that function's signature).
 *
 *  Rather than teach the lake nudge about runs, this is a general post-pass:
 *  whatever put a trunk past the shoreline, it ends up on the bank. It is
 *  LATERAL-only and outward-to-inward, the same rule the nudge follows — the
 *  park's ground follows the road's elevation along the course, so a sideways
 *  move keeps a trunk at exactly the height it was planted at. Deterministic,
 *  geometric, no rng.
 *
 *  Returns how many instances it moved (tests assert this hits zero on a city
 *  with no course water, and is non-zero on NYC before the clamp). */
export function clampVegetationToShore(
  groups: readonly { x: number; z: number }[][],
  ponds: readonly FlatInst[],
  track: WaterTrackLike,
  cfg: WaterCfg,
): number {
  const shore = waterShorelines(ponds, track, cfg);
  let moved = 0;
  for (const group of groups) {
    for (const t of group) {
      const p = track.project(t.x, t.z);
      const side: -1 | 1 = p.lateral >= 0 ? 1 : -1;
      const inner = shore(p.distance, side);
      if (inner == null) continue;
      // Never push a trunk into the road: the bank can be thinner than the
      // margin near a taper, and a tree on the racing line is a worse bug
      // than a tree with wet feet.
      const floor = track.halfWidth + WATER.pondRoadMargin;
      const limit = Math.max(floor, inner - SHORE_TREE_MARGIN);
      const lat = Math.abs(p.lateral);
      if (lat <= limit) continue;
      const s = track.sampleAt(p.distance);
      const pull = lat - limit;
      t.x -= s.rightX * side * pull;
      t.z -= s.rightZ * side * pull;
      moved++;
    }
  }
  return moved;
}
